import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { getContrastTextColor } from '../../utils/theme';
import { Mode } from '../../constants/enums';

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    fontWeight: 700,
    background:
      theme.palette.mode === Mode.Dark
        ? theme.palette.background.default
        : theme.palette.primary.main,
    padding: '5px 10px',
    boxSizing: 'border-box',
    borderRadius: '5px',
    fontSize: 'small',
    color:
      theme.palette.mode === Mode.Dark
        ? theme.palette.common.white
        : theme.palette.secondary.contrastText,
    '& svg': {
      width: '20px !important',
      height: '20px !important',
      '& path': {
        fill: theme.palette.common.white,
      },
    },
  },
  chipContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '10px',
    alignItems: 'center',
  },
  ticket: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '15px',
    background: theme.palette.background.paper,
    borderRadius: '5px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    border: '1px solid transparent',
    width: '100%',
    '&:hover': {
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      border: '1px solid #0000002b',
    },
  },
  ticketsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '10px',
    boxSizing: 'border-box',
    overflowY: 'hidden',
    height: '100vh',
    width: 665,
  },
  ticketTitle: {
    fontSize: '1.1rem !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.primary,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: 1,
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: '10px',
    overflowX: 'hidden',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& h5': {
      color: theme.palette.text.primary,
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '0 10px',
  },
  searchIcon: {
    width: '25px !important',
    height: '25px !important',
    cursor: 'pointer',
    '& svg': {
      width: '25px !important',
      height: '25px !important',
    },
    '& path': {
      fill: theme.palette.text.primary,
    },
  },
  chat: {
    display: 'flex',
    gap: '10px',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  chatMessage: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    padding: '5px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    width: '100%',
    color: theme.palette.text.primary,
  },
  chatMessageSender: {
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: '1',
  },
  chatMessageDate: {
    fontSize: 'small',
    color: theme.palette.text.secondary,
  },
  active: {
    background:
      theme.palette.mode === Mode.Dark ? theme.palette.common.black : theme.palette.primary.main,
    '& p': {
      color:
        theme.palette.mode === Mode.Dark
          ? theme.palette.common.white
          : theme.palette.secondary.contrastText,
    },
    color:
      theme.palette.mode === Mode.Dark
        ? theme.palette.common.white
        : theme.palette.secondary.contrastText,
  },
  container: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  ticketDetails: {
    height: '100vh',
    width: '100%',
    flex: '1',
  },
  timelineContainer: {
    flex: '1',
    overflowY: 'hidden',
    maxHeight: '100%',
  },
  ticketInfo: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '10px',
  },
  ticketInfoTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '80%',
    color: theme.palette.text.primary,
  },
  ticketDetailsContainer: {
    width: 'calc(100% - 695px)',
    marginRight: '20px',
    background: theme.palette.background.paper,
    padding: '20px',
    boxSizing: 'border-box',
    flex: '1',
    borderRadius: '5px',
    height: 'calc(100vh - 150px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  ticketFilters: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    padding: '10px',
  },
  activeChip: {
    cursor: 'pointer',
  },
  selectedChip: {
    background: 'var(--color-primary)',
    color: theme.palette.secondary.contrastText,
    '& svg': {
      '& path': {
        fill: theme.palette.secondary.contrastText,
      },
    },
  },
  multiSwitch: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    '& > :first-child': {
      borderRadius: '5px 0 0 5px',
    },
    '& > :last-child': {
      borderRadius: '0 5px 5px 0',
    },
  },
  chatContainer: {
    flex: '1',
    overflowY: 'auto',
    maxHeight: '100%',
    height: '100%',
  },
  noNotes: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
    color: theme.palette.text.primary,
    flexWrap: 'wrap',
    '& img': {
      width: '350px',
      height: '350px',
      filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : undefined,
    },
  },
  startChat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
    '& svg': {
      width: '20px',
      height: '20px',
      marginLeft: '5px',
    },
  },
  ticketListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chatList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    height: '90%',
    overflowY: 'auto',
  },
  chatListItem: {
    padding: '15px',
    background: theme.palette.primary.main,
    borderRadius: '5px',
    color: theme.palette.secondary.contrastText,
    display: 'flex',
    justifyContent: 'space-between',
    gridTemplateColumns: '50px 1fr 200px',
    alignItems: 'center',
    transition: 'filter 0.5s',
    cursor: 'pointer',
    '&:hover': {
      filter: `drop-shadow(0px 4px 4px ${theme.palette.primary.main})`,
    },
  },
  chatListItemClosed: {
    paddingLeft: '1px',
  },
  chatLockIcon: {
    margin: '0px 10px',
    width: '20px',
    height: '20px',
  },
  chatLockIconContainer: {
    display: 'flex',
    '& path': {
      fill:
        theme.palette.mode === Mode.Dark
          ? theme.palette.common.white
          : theme.palette.secondary.contrastText,
    },
  },
  outlinedButton: {
    textTransform: 'capitalize',
    alignItems: 'center',
    display: 'flex',
  },
  startChatButton: {
    '&&': {
      color: theme.palette.text.primary,
      borderColor: theme.palette.text.primary,
      '& path': {
        fill: theme.palette.text.primary,
      },
      '&:hover': {
        backgroundColor: 'transparent',
        borderColor: theme.palette.text.primary,
      },
    },
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  noTickets: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  contrastText: {
    color:
      theme.palette.mode === Mode.Dark
        ? theme.palette.common.white
        : theme.palette.secondary.contrastText,
  },
  filterGroupHeader: {
    fontSize: '1.1rem !important',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: '10px',
  },
  filters: {
    backgroundColor: 'transparent',
    '& .MuiToggleButtonGroup-root': {
      display: 'flex',
      flexDirection: 'row',
    },
    '& .Mui-selected': {
      background: 'var(--color-primary)',
      color: theme.palette.secondary.contrastText,
      '& svg': {
        '& path': {
          fill: theme.palette.secondary.contrastText,
        },
      },
    },

    '& .MuiButtonBase-root': {
      width: 'fit-content',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      fontWeight: 700,
      background: getContrastTextColor(theme.palette.primary.main, '#6e6e6e', '#ebe6da'),
      padding: '5px 10px',
      boxSizing: 'border-box',
      borderRadius: '5px !important',
      fontSize: 'small',
      color: 'var(--color-primary)',
      '& svg': {
        width: '20px !important',
        height: '20px !important',
        '& path': {
          fill: 'var(--color-primary)',
        },
      },
    },
  },
  startButton: {
    '& svg': {
      marginLeft: '10px',
      width: '20px',
      height: '20px',
    },
  },
}));

export default useStyles;
